import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { MapPin, Phone, Mail, Globe, Calendar, ArrowRight, CheckCircle, XCircle } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const DAYS_LABELS = {
  lunes: "Lunes", martes: "Martes", miercoles: "Miércoles", 
  jueves: "Jueves", viernes: "Viernes", sabado: "Sábado", domingo: "Domingo"
};

export default function BarProfile() {
  const { barId } = useParams();
  const [bar, setBar] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openStatus, setOpenStatus] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [barRes, eventsRes] = await Promise.all([
          axios.get(`${API}/bars/${barId}`),
          axios.get(`${API}/bars/${barId}/events`)
        ]);
        setBar(barRes.data);
        setEvents(eventsRes.data);
        
        // Track bar view for analytics
        axios.post(`${API}/bars/${barId}/track?analytics_type=view`).catch(() => {});
        
        // Check if open now
        const openRes = await axios.get(`${API}/bars/${barId}/is-open`);
        setOpenStatus(openRes.data);
      } catch (error) {
        console.error("Error fetching bar:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [barId]);

  const formatEventDate = (date) => {
    try {
      return format(new Date(date), "EEEE d 'de' MMMM, HH:mm", { locale: es });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-64 bg-[#111] mb-8" />
            <div className="h-8 bg-[#111] w-1/3 mb-4" />
            <div className="h-4 bg-[#111] w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!bar) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-black text-white mb-4">BAR NO ENCONTRADO</h1>
          <Link to="/bars" className="text-primary font-mono">
            VOLVER A BARES
          </Link>
        </div>
      </div>
    );
  }

  const hasLocation = bar.latitude && bar.longitude;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        {bar.image_url ? (
          <img
            src={bar.image_url}
            alt={bar.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1679205691779-4683ed620470?crop=entropy&cs=srgb&fm=jpg&q=85')"
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        {/* Bar Info */}
        <div className="bg-[#111] border border-zinc-800 p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tighter" data-testid="bar-name">
              {bar.name}
            </h1>
            
            {/* Open/Closed Status */}
            {openStatus && openStatus.is_open !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 ${
                openStatus.is_open ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
              }`} data-testid="bar-open-status">
                {openStatus.is_open ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span className="font-mono text-sm">{openStatus.message}</span>
              </div>
            )}
          </div>
          
          {bar.description && (
            <p className="text-zinc-400 mb-6 max-w-3xl" data-testid="bar-description">
              {bar.description}
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {bar.address && (
              <div className="flex items-start gap-3 bg-[#0a0a0a] p-4 border border-zinc-800">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="block font-mono text-xs text-zinc-500 mb-1">DIRECCIÓN</span>
                  <span className="text-sm text-white" data-testid="bar-address">{bar.address}</span>
                </div>
              </div>
            )}
            {bar.phone && bar.show_phone !== false && (
              <div className="flex items-start gap-3 bg-[#0a0a0a] p-4 border border-zinc-800">
                <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="block font-mono text-xs text-zinc-500 mb-1">TELÉFONO</span>
                  <a href={`tel:${bar.phone}`} className="text-sm text-white hover:text-primary" data-testid="bar-phone">
                    {bar.phone}
                  </a>
                </div>
              </div>
            )}
            {bar.email && bar.show_email !== false && (
              <div className="flex items-start gap-3 bg-[#0a0a0a] p-4 border border-zinc-800">
                <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="block font-mono text-xs text-zinc-500 mb-1">EMAIL</span>
                  <a href={`mailto:${bar.email}`} className="text-sm text-white hover:text-primary" data-testid="bar-email">
                    {bar.email}
                  </a>
                </div>
              </div>
            )}
            {bar.contact && bar.show_contact !== false && (
              <div className="flex items-start gap-3 bg-[#0a0a0a] p-4 border border-zinc-800">
                <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="block font-mono text-xs text-zinc-500 mb-1">CONTACTO</span>
                  {bar.contact.startsWith("http") || bar.contact.includes("@") ? (
                    <a 
                      href={bar.contact.includes("@") ? `mailto:${bar.contact}` : bar.contact}
                      target={bar.contact.includes("@") ? undefined : "_blank"}
                      rel={bar.contact.includes("@") ? undefined : "noopener noreferrer"}
                      className="text-sm text-white hover:text-primary truncate block" 
                      data-testid="bar-contact"
                    >
                      {bar.contact}
                    </a>
                  ) : (
                    <span className="text-sm text-white" data-testid="bar-contact">
                      {bar.contact}
                    </span>
                  )}
                </div>
              </div>
            )}
            {bar.website && (
              <div className="flex items-start gap-3 bg-[#0a0a0a] p-4 border border-zinc-800">
                <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="block font-mono text-xs text-zinc-500 mb-1">WEB</span>
                  <a 
                    href={bar.website.startsWith("http") ? bar.website : `https://${bar.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white hover:text-primary truncate block"
                    data-testid="bar-website"
                  >
                    {bar.website}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Opening Hours */}
        {bar.opening_hours && bar.opening_hours.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-black text-white mb-4 tracking-tighter">
              HORARIO
            </h2>
            <div className="bg-[#111] border border-zinc-800 p-6 max-w-md">
              <div className="space-y-2">
                {DAYS_OF_WEEK.map((day) => {
                  const hours = bar.opening_hours.find(h => h.day === day);
                  const isToday = new Date().getDay() === (DAYS_OF_WEEK.indexOf(day) + 1) % 7;
                  return (
                    <div 
                      key={day} 
                      className={`flex justify-between items-center py-2 px-3 ${
                        isToday ? "bg-primary/10 border-l-2 border-primary" : ""
                      }`}
                    >
                      <span className={`font-mono text-sm ${isToday ? "text-white" : "text-zinc-400"}`}>
                        {DAYS_LABELS[day]}
                      </span>
                      <span className={`font-mono text-sm ${
                        hours?.is_closed ? "text-red-400" : isToday ? "text-white" : "text-zinc-300"
                      }`}>
                        {hours?.is_closed ? "CERRADO" : hours ? `${hours.open_time} - ${hours.close_time}` : "-"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Map */}
        {hasLocation && (
          <div className="mb-8">
            <h2 className="text-2xl font-black text-white mb-4 tracking-tighter">
              UBICACIÓN
            </h2>
            <div className="h-64 sm:h-80 border border-zinc-800 overflow-hidden" data-testid="bar-map">
              <MapContainer
                center={[bar.latitude, bar.longitude]}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={[bar.latitude, bar.longitude]}>
                  <Popup>
                    <strong>{bar.name}</strong>
                    {bar.address && <br />}
                    {bar.address}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        )}

        {/* Events */}
        {(() => {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          
          // Separate future and past events
          const futureEvents = events
            .filter(e => new Date(e.event_date) >= now)
            .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
          
          const pastEvents = events
            .filter(e => new Date(e.event_date) < now)
            .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
          
          return (
            <>
              {/* Upcoming Events */}
              <div className="pb-8">
                <h2 className="text-2xl font-black text-white mb-4 tracking-tighter">
                  PRÓXIMOS <span className="text-primary">EVENTOS</span>
                </h2>

                {futureEvents.length > 0 ? (
                  <div className="space-y-4">
                    {futureEvents.map((event) => (
                      <Link
                        key={event.event_id}
                        to={`/events/${event.event_id}`}
                        className="group card-ruido bg-[#111] border border-zinc-800 p-6 flex flex-col sm:flex-row gap-6"
                        data-testid={`bar-event-${event.event_id}`}
                      >
                        {event.image_url && (
                          <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden">
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="font-mono text-sm text-primary">
                              {formatEventDate(event.event_date)}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                            {event.title}
                          </h3>
                          {event.description && (
                            <p className="text-zinc-500 text-sm line-clamp-2">{event.description}</p>
                          )}
                          {event.price && (
                            <span className="inline-block mt-3 bg-zinc-800 px-3 py-1 font-mono text-sm text-white">
                              {event.price}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="bg-[#111] border border-zinc-800 p-8 text-center">
                    <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 font-mono text-sm">NO HAY EVENTOS PROGRAMADOS</p>
                  </div>
                )}
              </div>

              {/* Past Events */}
              {pastEvents.length > 0 && (
                <div className="pb-12">
                  <h2 className="text-2xl font-black text-zinc-500 mb-4 tracking-tighter">
                    EVENTOS <span className="text-zinc-600">PASADOS</span>
                  </h2>

                  <div className="space-y-4">
                    {pastEvents.map((event) => (
                      <Link
                        key={event.event_id}
                        to={`/events/${event.event_id}`}
                        className="group bg-[#111]/50 border border-zinc-800/50 p-6 flex flex-col sm:flex-row gap-6 opacity-60 hover:opacity-80 transition-opacity"
                        data-testid={`bar-past-event-${event.event_id}`}
                      >
                        {event.image_url && (
                          <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden grayscale">
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Calendar className="w-4 h-4 text-zinc-600" />
                            <span className="font-mono text-sm text-zinc-600">
                              {formatEventDate(event.event_date)}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-zinc-400 mb-2 group-hover:text-zinc-300 transition-colors">
                            {event.title}
                          </h3>
                          {event.description && (
                            <p className="text-zinc-600 text-sm line-clamp-2">{event.description}</p>
                          )}
                          {event.price && (
                            <span className="inline-block mt-3 bg-zinc-800/50 px-3 py-1 font-mono text-sm text-zinc-500">
                              {event.price}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          <ArrowRight className="w-5 h-5 text-zinc-700 group-hover:text-zinc-500 transition-all" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
